import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { HttpLink } from 'apollo-link-http'
import { WebSocketLink } from 'apollo-link-ws'
import { RetryLink } from 'apollo-link-retry'
import { onError } from 'apollo-link-error'
import { getMainDefinition } from 'apollo-utilities'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { persistCache } from 'apollo-cache-persist'
import { withClientState } from 'apollo-link-state'
import { getAccessToken, isLoggedIn, clearSession } from '@/lib/auth'
import { errorNotification } from '@/lib/errors'
import fetch from 'unfetch'

import Vue from 'vue'
import VueApollo from 'vue-apollo'

Vue.use(VueApollo)

// Apollo setup
const httpLink = new HttpLink({
  uri: process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql',
  fetch: fetch
})

const wsLink = new WebSocketLink({
  uri: process.env.VUE_APP_API_WS_ENDPOINT || 'wss://apiv2.leaf.ai/graphql',
  options: {
    lazy: true,
    reconnect: true,
    connectionParams: () => ({
      authorization: "Bearer " + getAccessToken()
    })
  }
})

const terminatingLink = ApolloLink.split(
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return (
      kind === 'OperationDefinition' && operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
)

const cache = new InMemoryCache({
  addTypename: false
})

persistCache({
  cache,
  storage: window.localStorage,
  maxSize: 2097152 // 2mb
})

const stateLink = withClientState({
  cache,
  resolvers: {
    Mutation: {
      updateConfig: (_, { config }, { cache }) => {
        const data = {
          config: {
            ...config,
            __typename: "Config"
          }
        }
        cache.writeData({ data })
        return null
      }
    }
  },
  defaults: {
    config: {
      __typename: 'Config',
      plunge: {
        __typename: 'Plunge',
        refreshEnabled: false,
        aggMethod: {
          __typename: 'PlungeAggregation',
          period: '15m',
          fn: 'mean'
        },
        selectedDevices: []
      },
      flume: {
        __typename: 'Flume',
        thresholdsEnabled: true,
        refreshEnabled: false,
        aggMethod: {
          __typename: 'FlumeAggregation',
          period: '1h',
          fn: 'mean'
        },
        selectedDevices: []
      },
      well: {
        __typename: 'Well',
        refreshEnabled: false,
        aggMethod: {
          __typename: 'WellAggregation',
          period: '15m',
          fn: 'mean'
        },
        selectedDevices: []
      },
      bubbler: {
        __typename: 'Bubbler',
        refreshEnabled: false,
        aggMethod: {
          __typename: 'BubblerAggregation',
          period: '15m',
          fn: 'mean'
        },
        selectedDevices: []
      },
      river: {
        __typename: 'River',
        refreshEnabled: false,
        aggMethod: {
          __typename: 'RiverAggregation',
          period: '15m',
          fn: 'mean'
        },
        selectedDevices: []
      },
    }
  }
})

const authMiddleware = new ApolloLink((operation, forward) => {
  if (isLoggedIn()) {
    operation.setContext({
      headers: {
        authorization: getAccessToken() ? `Bearer ${getAccessToken()}` : ''
      }
    })
  }
  return forward(operation)
})


const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map((error) => {
      if (error.message == 'Not Authorized') {
        errorNotification(error.message)
        // clearSession()
      } else {
        // console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        errorNotification(error.message)
      }
    })
  }

  if (networkError) {
    if (networkError.statusCode && networkError.statusCode == 400) {
      try {
        if (networkError.result.errors[0].extensions.code === 'UNAUTHENTICATED') {
          errorNotification('Session Invalid, please login again')
          clearSession()
        }
      } catch (e) {
        // pass
      }
    } else {
      errorNotification('Could not connect to backend service. Retrying...')
    }
  }
})

const apolloClient = new ApolloClient({
  link: ApolloLink.from([new RetryLink(), authMiddleware, stateLink, errorLink, terminatingLink]),
  cache,
  connectToDevTools: true
})

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
  defaultOptions: {
    $loadingKey: 'loading'
  }
})
